/**
 * 배달앱 메뉴 동기화 화면의 **안내 문구 정본** — 의존성이 없는 모듈이다.
 *
 * 왜 따로 있는가: 이 문장들은 사장님이 읽는 계약이라 화면·테스트가 **같은 하나**를 봐야 한다.
 * 그런데 `deliveryMenuSync.js` 는 `@/mixins/utilMixin` 을 끌어와 앱 번들 밖(cypress 전처리기)에서는
 * 해석되지 않는다. 문구가 거기 살면 e2e 는 문장을 **다시 타이핑**할 수밖에 없고, 그 사본이 곧
 * 사실의 두 번째 출처가 된다 — 문구를 한 번 고치면 사본 N 개를 손으로 맞춰야 하고, 하나를 빠뜨리면
 * 화면 결함이 아니라 CI 실패로 나타난다(2026-08-20 에 실제로 그렇게 깨졌다).
 *
 * 그래서 여기에는 **의존성 없는 모듈의 상대 경로 import(`.js` 확장자까지)만** 들인다 — 지금은 플랫폼 표
 * (`deliveryPlatform.js`, 그 자신도 아무것도 import 하지 않는다) 하나다. `@/` 별칭·Vue·`deliveryMenuSync.js` 를
 * 끌어오는 순간 분리한 이유가 사라진다.
 */

import { platformDisplayName } from './deliveryPlatform.js'

/** 필드 코드 — `deliveryMenuSync.js` 의 `DELIVERY_MENU_FIELD` 가 이 값을 그대로 쓴다 */
export const PICKUP_PRICE_FIELD = 'pickupPrice'
/** 메뉴 사용 여부 — 오더엔의 삭제·「사용 안 함」이 둘 다 이 축(`use_yn='N'`)이다 (규약 §16.302) */
export const USE_YN_FIELD = 'useYn'
/** 옵션 항목 사용 여부 — 같은 사건의 옵션 축. `deliveryMenuSync.js` 의 필드 어휘·라벨이 이 둘을 읽는다 */
export const OPTION_USE_YN_FIELD = 'optionUseYn'
export const OPTION_USE_YN_LABEL = '옵션 사용 여부'

/** 채널 코드 */
export const DELIVERY_MENU_CHANNEL = {
  DELIVERY: 'DELIVERY',
  PICKUP: 'PICKUP',
}

/**
 * 링크·작업 상태 어휘 — 서버 enum(`DeliveryMenuLinkState`·`DeliveryMenuItemStatus`)과 같은 문자열이다.
 *
 * chip 표(`deliveryMenuSync.js`)와 POS 임베드 화면의 판정(`posDeliverySoldout.js`)이 같은 어휘를 읽는다. 여기(의존성
 * 없는 모듈)에 두는 이유는 문구와 같다 — 앱 번들 밖(단위 테스트·cypress)에서 그대로 읽을 수 있어야 한다.
 * `deliveryMenuSync.js` 가 `export *` 로 다시 내보내므로 기존 import 경로(`@/utils/deliveryMenuSync`)는 그대로다.
 */
/** 링크 상태 어휘 (`DeliveryMenuLinkState`). `null` 은 "링크 없음" 이고 이 목록에 없다 */
export const LINK_STATE = {
  LINKED: 'LINKED',
  AMBIGUOUS: 'AMBIGUOUS',
  STALE: 'STALE',
  ORPHAN: 'ORPHAN',
}

/** item 상태 어휘 (`DeliveryMenuItemStatus`) */
export const ITEM_STATUS = {
  NOT_LINKED: 'NOT_LINKED',
  DISABLED: 'DISABLED',
  BLOCKED_LINK: 'BLOCKED_LINK',
  UNSUPPORTED: 'UNSUPPORTED',
  DRIFT_BLOCKED: 'DRIFT_BLOCKED',
  WAITING_EXECUTOR: 'WAITING_EXECUTOR',
  WAITING_AUTH: 'WAITING_AUTH',
  QUEUED: 'QUEUED',
  CLAIMED: 'CLAIMED',
  EXECUTING: 'EXECUTING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  OUTCOME_UNKNOWN: 'OUTCOME_UNKNOWN',
  SUPERSEDED: 'SUPERSEDED',
  EXPIRED: 'EXPIRED',
}

/** 옵션 항목 지목 결과 어휘 (`option-status` 의 `match`) — 옵션을 관측 그래프에서 **이름으로** 찾은 결과 (D6) */
export const OPTION_MATCH = Object.freeze({ EXACT: 'EXACT', AMBIGUOUS: 'AMBIGUOUS', MISSING: 'MISSING' })

/**
 * **포장 축 반영의 선행 조건 — 배민.** 이름은 땡겨요 이전부터 규약·시험이 부르는 그대로 두고, 플랫폼별 표는
 * 아래 [PICKUP_PUSH_CONDITIONS] 다(이 상수는 그 표의 배민 칸이다).
 *
 * 배민이 강제하는 것은 둘이다 — ① 매장가격이 설정돼 있을 것, ② 포장가가 매장가격 이하일 것
 * (서버 문구: "매장가격은 픽업가격보다 같거나 높아야 해요"). 매장가격이 없으면 배민이 포장가를
 * 배달가에 종속시킨다.
 *
 * **이 문구는 실패한 뒤가 아니라 값을 정하는 자리에서 뜬다.** 실패는 화면에 `해당 변경 미지원`
 * 으로만 도착한다 — 룰이 보낸 사유(`OFFLINE_PRICE_REQUIRED`)를 서버가 `result_message` 에 담지
 * 않기 때문이다(§9.2 — 실행자 자유 문자열은 §4.8 감사로만 간다). 사장님에게 필요한 것은
 * "왜 안 됐나" 보다 **"무엇을 해야 되나"** 이므로, 조건을 미리 말하는 것이 그 자리를 대신한다.
 */
export const PICKUP_PUSH_CONDITION =
  '배민은 매장가격이 설정된 메뉴에서만, 매장가격 이하로만 포장 가격이 반영됩니다 — 매장가격이 없으면 포장 가격은 배달 가격을 따라갑니다'

/**
 * **포장 축 반영의 선행 조건 — 플랫폼별** (§16.171 · §16.307). 포장 축이 있는 플랫폼(플랫폼 표의 `pickupChannel`)마다
 * 한 문장이고, **그 플랫폼의 문장만** 붙는다 — 배민 행에 땡겨요 조건이 붙으면 없는 걱정이다(§16.7).
 *
 * 땡겨요 — 포장 가격은 가게에 **배달과 포장이 함께 켜져 있을 때만** 따로 바뀐다. 포장 서비스가 꺼진 가게, 그리고
 * 배달이 꺼져 포장 가격이 그 가게의 기본 가격인 가게에서는 룰이 그 축만 `CHANNEL_UNAVAILABLE` 로 건너뛰고(같은 요청의
 * 다른 축은 그대로 나간다) 포장 가격을 따로 관측하지도 않는다 — 배달이 꺼진 가게의 포장 가격은 메뉴 가격을 따른다
 * (omni `ad8a1c2` 의 `pickupChannel` 판정, `ddangyo-boss-parity.md` 「복수 가격」·`ddangyo-boss-sync.md` 「세션·가게 계약」).
 */
export const PICKUP_PUSH_CONDITIONS = Object.freeze({
  BAEMIN: PICKUP_PUSH_CONDITION,
  DDANGYO:
    '땡겨요는 배달과 포장이 함께 켜진 가게에서만 포장 가격이 따로 반영됩니다 — 배달이 꺼진 가게는 포장 가격이 기본 가격이라 메뉴 가격을 따릅니다',
})

/**
 * ★★ **오더엔 삭제 = 배달앱 숨김** — 3사 통일 (규약 §16.302).
 *
 * 오더엔의 「삭제」와 「사용 안 함」은 둘 다 `use_yn='N'`(소프트 삭제)이라 **같은 사건**이고, 배달앱
 * (배민·요기요·쿠팡이츠)에서는 그 메뉴·옵션 항목을 **숨긴다**. 하드 삭제는 여전히 닫혀 있다(§7.3) —
 * 숨김은 가역이라 그 결정을 뒤집지 않는다. 되돌리기는 「사용」이며, 그때 배달앱에서는 **판매중**이
 * 된다 — 배민은 숨김 해제와 품절 해제가 같은 요청(`…/status/menus/active`)이라 분리가 불가능하고
 * (§16.232①), 나머지 두 곳도 같은 결과로 맞춘다.
 *
 * ★ **부기는 범위만 말하고 개통을 단정하지 않는다.** 이 부기가 그려지는 자리(④ 「참고(차단 아님)」
 * 열)는 capability 가 **닫힌** 축의 차이라, 「숨깁니다」라고 쓰면 그 자리에서 곧 거짓이 된다. 그래서
 * 문장은 「숨김이 배달앱에 나갈 때 …」 조건절이고, **그 행의 플랫폼 문장만** 붙는다 — 배민 행에
 * 요기요 문장이 붙으면 없는 걱정이다(§16.7). 쿠팡이츠는 갈리는 사실이 없어 문장이 없다.
 *
 * 플랫폼마다 다른 사실: 배민 — 숨김 스코프가 가게가 아니라 카탈로그라 형제 가게까지 내려가고
 * (§16.232②), 숨김 해제가 품절 해제와 같은 문이다(§16.232①). 요기요 — 숨김이 하루품절을 **덮어써**
 * 만료가 사라진다(§16.291). 땡겨요 — 채널 구분 없는 숨김(`useYn`)이 가게에 **켜진 서비스(배달·포장·매장)
 * 전부**에 걸리고, 그 서비스가 모두 숨겨져 있어야 숨김으로 관측된다(§16.307 Medium 검토 보완 ·
 * `ddangyo-boss-sync.md` 「세션·가게 계약」). 땡겨요의 일반 삭제도 같은 `useYn` 숨김이다(§16.307).
 */
export const HIDE_SCOPE_NOTES = {
  BAEMIN:
    '숨김이 배달앱에 나갈 때 배민은 가게가 아니라 메뉴 단위로 숨겨져 같은 메뉴를 쓰는 다른 가게(형제 매장)도 함께 내려가고, 숨김 해제와 품절 해제가 같은 요청이라 숨김을 풀면 품절로 둘 수 없고 바로 판매중이 됩니다',
  YOGIYO:
    '숨김이 배달앱에 나갈 때 요기요는 숨김이 하루품절을 덮어써, 하루품절 중인 메뉴를 숨기면 다음날 자동으로 풀리지 않고 숨김으로 남습니다',
  DDANGYO:
    '숨김이 배달앱에 나갈 때 땡겨요는 가게에 켜져 있는 서비스(배달·포장·매장)마다 함께 숨겨지고, 그 서비스가 모두 숨겨져 있을 때만 숨김으로 확인됩니다',
}

/**
 * **삭제 확인창의 배달앱 한 줄** (§16.302).
 *
 * 링크가 있을 때만 단정한다 — 리얼 활성 매장 1,219곳 중 배달 링크가 있는 곳은 9곳이라 언제나 띄우면
 * 없는 걱정을 만든다(§16.7). 화면이 그 사실을 **모르면**(배달 상태를 아직 안 읽었거나 읽을 권한이
 * 없으면) 조건문으로 말한다 — 모르는 것을 아는 척하지 않는다(§16.216). 옵션그룹 삭제에는 붙지 않는다 —
 * 그 삭제는 숨김이 아니라 **연결 해제**다(§16.302 §1-3).
 *
 * ★ **배포 순서** — 이 문장은 capability 개통과 무관하게 뜬다(화면은 capability 를 읽지 않는다).
 * 「숨김 처리됩니다」가 참이려면 서버 capability(`UPDATE/useYn` 3사 · `UPDATE/optionUseYn` 3사)가
 * 그 환경에 켜져 있어야 하므로, **capability 개통 뒤에 배포한다**(dev 는 이 회차 seed · real 은 별도 승인).
 * 땡겨요(§16.307)는 같은 두 축이 **비활성으로** seed 돼 있어, 그 행을 켜기 전에는 땡겨요 링크에 대해 이 문장이
 * 아직 참이 아니다 — 개통은 capability 활성화가 정한다(이 화면은 capability 를 읽지 않는다).
 */
const DELETE_HIDES_HEAD = '연동된 배달앱에서도 숨김 처리됩니다'
const DELETE_HIDES_HEAD_IF_LINKED = '연동된 배달앱이 있으면 그 배달앱에서도 숨김 처리됩니다'
const DELETE_HIDES_TAIL = '되돌리려면 「사용」으로 다시 켜세요'
/**
 * ★ **옵션 항목은 그룹 반경으로 나간다** (§16.302 §3). 옵션 축(`optionUseYn`)의 실행 대상이 옵션그룹이라
 * 목표에 그 그룹의 **모든** 항목 사용 여부가 실리고, 배달앱에서 어긋나 있던 다른 항목도 오더엔대로 맞춰진다.
 * 항목 하나를 지우는 사장님이 그 반경을 모르면 「안 건드린 옵션이 바뀌었다」를 겪는다.
 */
export const OPTION_DELETE_GROUP_SCOPE = '이 그룹의 다른 옵션도 오더엔의 사용 여부대로 맞춰집니다'

export const DELETE_HIDES_ON_DELIVERY = `${DELETE_HIDES_HEAD}. ${DELETE_HIDES_TAIL}`
export const DELETE_HIDES_ON_DELIVERY_IF_LINKED = `${DELETE_HIDES_HEAD_IF_LINKED}. ${DELETE_HIDES_TAIL}`
export const OPTION_DELETE_HIDES_ON_DELIVERY = `${DELETE_HIDES_HEAD}. ${OPTION_DELETE_GROUP_SCOPE}. ${DELETE_HIDES_TAIL}`
export const OPTION_DELETE_HIDES_ON_DELIVERY_IF_LINKED = `${DELETE_HIDES_HEAD_IF_LINKED}. ${OPTION_DELETE_GROUP_SCOPE}. ${DELETE_HIDES_TAIL}`

const hidesNote = (linked, assertive, conditional) => {
  if (linked === false) return ''
  return linked === true ? assertive : conditional
}

/**
 * 메뉴 삭제 확인창의 한 줄.
 *
 * @param {boolean|null} linked 배달 링크가 있는가 — `true` 단정 · `false` 없음(한 줄도 붙이지 않는다) ·
 *   `null` 모른다(조건문)
 * @returns {string} 확인창에 붙일 문장. 없으면 빈 문자열
 */
export function deliveryDeleteHidesNote(linked) {
  return hidesNote(linked, DELETE_HIDES_ON_DELIVERY, DELETE_HIDES_ON_DELIVERY_IF_LINKED)
}

/** 옵션 항목 삭제 확인창의 한 줄 — 메뉴용과 같은 3상태에 그룹 반경([OPTION_DELETE_GROUP_SCOPE])이 더해진다 */
export function deliveryOptionDeleteHidesNote(linked) {
  return hidesNote(linked, OPTION_DELETE_HIDES_ON_DELIVERY, OPTION_DELETE_HIDES_ON_DELIVERY_IF_LINKED)
}

/**
 * 오더엔에서 내린 메뉴(`STALE` 링크) chip 의 부기 (§16.302) — 반영을 **단정하지 않는다.**
 *
 * 숨김 push 의 결과는 chip 뒤에 붙는 마지막 작업 상태가 말한다(`deliveryMenuChip`). 그 축이 열리기 전에
 * 내린 링크는 작업이 없고 배달앱에 그대로 남아 있으며, 드리프트는 자동으로 밀지 않는다(§11.2) — 그래서
 * 「반영됩니다」라고 쓰면 그 링크에서 거짓이다. 작업이 있으면 그 작업의 부기(서버 문장·표 기본값)가 이긴다.
 */
export const STALE_LINK_HIDDEN_NOTE =
  '오더엔에서 삭제하거나 「사용 안 함」으로 둔 메뉴입니다. 배달앱 숨김 반영 여부는 함께 표시된 상태가 말하며, 상태가 없으면 아직 반영 요청이 없었던 것입니다. 되돌리려면 「사용」으로 다시 켜세요'

/**
 * 필드에 붙는 **부기** — 포장 가격은 반영 조건(§16.171), 사용 여부는 숨김의 범위와 되돌리기(§16.302).
 *
 * 2026-08-20 에 배민 `UPDATE/pickupPrice` capability 가 생기면서 이 축의 차이는 더 이상
 * `unsupportedDiffFields[]` 로 오지 않는다 — 차단 근거(`fields[]`)로 온다. 그래도 부기를 남기는
 * 이유는 **반영이 되느냐가 메뉴마다 갈리기 때문**이다: 매장가격이 선 메뉴는 나가고, 없는 메뉴는
 * 룰이 그 축만 접는다. 그 조건을 말하지 않으면 사장님은 값을 맞추려고 배달앱을 고치게 되고,
 * 그러면 이번엔 배달가가 어긋난다.
 *
 * 사용 여부의 부기는 반영 조건이 아니라 **범위**다 — 배민은 형제 가게까지 내려가고 요기요는 하루품절을
 * 덮어쓴다. 그것을 말하지 않으면 사장님은 「내가 안 누른 가게가 내려갔다」「내일 풀릴 줄 알았다」를 겪는다.
 *
 * **두 축 다 플랫폼마다 문장이 갈린다** — 포장 조건은 배민(매장가격)과 땡겨요(포장 서비스)가 다르고(§16.307),
 * 숨김 범위도 플랫폼마다 다르다. 그래서 표는 `field → platform → 문장` 하나이고, 플랫폼 없이 부르면 답하지 않는다.
 */
const FIELD_PLATFORM_NOTES = {
  [PICKUP_PRICE_FIELD]: PICKUP_PUSH_CONDITIONS,
  [USE_YN_FIELD]: HIDE_SCOPE_NOTES,
}

/** 플랫폼별 표의 한 칸 — 그 플랫폼 키가 표에 **자기 것으로** 있을 때만 문장이다(물려받은 키를 문장으로 읽지 않는다) */
function platformNoteOf(byPlatform, platform) {
  if (!byPlatform || !platform) return null
  return Object.prototype.hasOwnProperty.call(byPlatform, platform) ? byPlatform[platform] : null
}

/**
 * **목표를 정하는 화면**에서의 부기 — 값을 정하는 그 자리에서 조건을 말한다 (§16.177).
 *
 * 필드 부기([FIELD_PLATFORM_NOTES])와 같은 사실이지만 동사가 다르다: 저기는 차이를 보여만 주고, 여기는
 * 사장님이 값을 정해 **저장한다**. "저장되지 않았다"로 읽히면 사장님은 같은 값을 계속 다시 넣는다.
 * 조건 문장은 같은 표([PICKUP_PUSH_CONDITIONS])에서 **그 화면에 입력칸이 열린 플랫폼**의 것만 잇는다.
 *
 * **배달 축에는 부기가 없다** — 2026-08-19 `F-1` 실측으로 배민 배달가 반영이 열려 3사 모두 목표가
 * 그대로 나간다. 열린 축에 조건을 붙이면 사장님은 반영된 값을 반영되지 않았다고 읽는다.
 */
const CHANNEL_PENDING_HEAD = '정하신 값은 저장되고, 조건을 갖춘 메뉴는 배달앱에 반영됩니다'

/** 채널 → 플랫폼별 조건 문장. 표에 없는 채널(배달)은 부기가 없다 */
const CHANNEL_PLATFORM_CONDITIONS = {
  [DELIVERY_MENU_CHANNEL.PICKUP]: PICKUP_PUSH_CONDITIONS,
}

/**
 * **「메뉴 가져오기」는 맞추기(align)라 되돌리기가 아니다** (§16.197⑤).
 *
 * 인바운드가 메뉴↔옵션 연결에 하는 일은 `MenuOptionLinkWriter.align` — 그 메뉴의 연결을 **전부
 * 지우고** 관측한 집합을 다시 넣는다(§16.195). 그래서 방향이 둘로 갈린다: 배달앱에만 있는 옵션은
 * 오더엔에 붙고, **오더엔에서 지운 옵션도 배달앱에 남아 있으면 다시 붙는다.** 사장님이 "지웠는데
 * 왜 또 생기나" 로 만나는 쪽이 뒤쪽이다.
 *
 * dev 링크 889(매장 596 · smenu 59922 · 배민)가 그 사실의 기록이다 — 오더엔 옵션 연결은 **0건**인데
 * 링크의 관측은 옵션 행 **23개**를 들고 있다. 다음 가져오기가 그 23개를 그대로 붙인다.
 * (그 관측을 담는 컬럼은 §16.198 에서 `observed_smenu_opt_cds` → `observed_option_groups` 로 승격했다 —
 * 그룹 경계를 갖게 됐을 뿐 **이 문장이 말하는 맞추기의 의미는 그대로다.**)
 *
 * **이 문장은 옵션 연결 아웃바운드가 열린 뒤에도 참이다** (그 축의 안내는 [optionOutboundNotes] 가
 * capability 에서 파생한다 — 상수 `OPTION_OUTBOUND_SCOPE` 는 §16.214 가 없앴다). 반영이 되는
 * 링크에서는 오더엔에서 뗀 그룹이 배달앱에서도 떨어지므로 되붙을 것이 없지만, 축이 접힌 링크
 * (관측 없음·오더엔 전용 그룹·같은 이름 둘)에서는 배달앱에 그대로 남아 다음 가져오기가 되붙인다.
 * (요기요가 여기서 빠진 것은 §16.199 에서 그 플랫폼이 함께 열렸기 때문이고, 대신 이름 축이
 * 새로 만든 갈래(같은 이름 둘)가 들어왔다.)
 */
export const OPTION_IMPORT_REALIGNS =
  '메뉴 가져오기는 배달앱 값으로 맞추는 것이라, 오더엔에서 지운 옵션이라도 배달앱에 남아 있으면 다시 붙습니다'

/**
 * ★★ **옵션 아웃바운드 안내는 문자열이 아니라 파생이다** (§16.214).
 *
 * ## 왜 상수를 없앴는가
 *
 * 종전 `OPTION_OUTBOUND_SCOPE` 는 *"옵션그룹·옵션 자체(그룹 이름·옵션 항목·추가금액·선택 개수)를
 * 만들거나 고치는 것은 **어느 배달앱에도 반영되지 않으니** 각 배달앱의 사장님 사이트에서 고쳐
 * 주세요"* 라고 **단언**했다. 그것은 capability 표의 상태를 손으로 옮겨 적은 **두 번째 사본**이었고,
 * 표가 바뀔 때 같이 바뀔 경로가 없었다.
 *
 * §16.203·§16.204 로 품절·선택 개수·추가금액이 3사 전부 나가게 되면서 **dev 에서 이미 거짓**이 됐고,
 * 리얼 capability 를 켜는 순간 리얼에서도 거짓이 된다. **같은 사고가 §16.198→§16.199 에서 이미 한 번
 * 났다** — 그때 고친 것이 문장뿐이라 자리가 그대로 남았다. 세 번째를 막으려면 문장이 **자기 근거를
 * 봐야** 한다.
 *
 * 그래서 서버가 `fieldSupport[]`(플랫폼별 supported·unsupported 축)를 내리고, 이 함수가 그것으로
 * 문장을 만든다. **거짓이 될 수 없다** — 축이 열리면 문장도 함께 열린다.
 *
 * ## 규칙
 *
 * 1. **이 메뉴가 실제로 올라간 플랫폼으로 먼저 좁힌다.** 올라가 있지 않은 플랫폼의 capability 는
 *    이 메뉴에 대해 **존재하지 않는 사실**이다(§16.7 — 없는 걱정을 만들지 않는다).
 * 2. **같은 답끼리 묶는다.** 3사 답이 같으면 한 줄이고(dev·real 지금은 그렇다), 갈리면
 *    「배민은 … / 요기요·쿠팡이츠는 …」 두 줄이 된다. 뭉개지 않으면서 답이 같을 때 줄을 늘리지 않는다.
 * 3. **어느 절이든 비면 만들지 않는다.** 빈 열거로 「…는 반영됩니다」를 쓰면 그 자체가 거짓이다.
 * 4. **라벨 없는 코드는 침묵한다.** 새 축이 열렸는데 화면이 늦으면 문장이 **불완전**해질 뿐
 *    **거짓**은 되지 않는다 — 지금 결함(거짓 단언)의 **반대 방향으로만** 실패하도록 고정한다.
 */
const OPTION_AXIS_LABEL = {
  optionGroups: '메뉴에 옵션그룹을 붙이고 떼기',
  optionSoldOut: '옵션 품절',
  optionSelectCount: '옵션 선택 개수(최소·최대)',
  optionItemPrice: '옵션 추가금액',
  /** 옵션 항목의 삭제·「사용 안 함」= 배달앱 숨김 (규약 §16.302) */
  [OPTION_USE_YN_FIELD]: OPTION_USE_YN_LABEL,
}

// 플랫폼 이름은 플랫폼 표의 표시명(`platformDisplayName`)이다 — 여기서 라벨 표를 다시 두지 않는다.

/**
 * **옵션 축이 열려 있을 때만 붙는 구조 조항** — capability 어휘에 코드가 없는 한계들이다.
 *
 * 그룹 이름 편집·항목 추가/삭제·그룹 생성은 축이 아니라 **연결 축의 구조 규칙**이라 파생 대상이
 * 아니다. 다만 `optionGroups` 가 열려 있지 않으면 말할 이유도 없으므로 조건부로 붙인다.
 */
const OPTION_NAME_IS_MATCH_KEY =
  '반영은 옵션그룹 이름으로 짝을 찾으므로 오더엔에서 그룹 이름을 바꾸면 그때부터 그 그룹은 반영되지 않습니다'
const OPTION_ORDERN_ONLY_FOLDS =
  '배달앱에 없는 옵션그룹이 이 메뉴에 붙어 있으면 그 메뉴는 옵션이 아예 반영되지 않습니다'

const axisLabels = function (fields) {
  return (fields || [])
    .map(function (f) {
      return OPTION_AXIS_LABEL[f]
    })
    .filter(Boolean)
}

/**
 * 옵션 아웃바운드 안내 줄들 — **capability 에서 파생한다.**
 *
 * @param {Array} fieldSupport 서버 응답의 `fieldSupport[]`
 * @param {Array} platforms 이 메뉴가 실제로 올라간 플랫폼 코드 목록. 비면 전체를 쓴다
 * @returns {Array<string>} 화면에 그대로 그릴 문장들. 말할 것이 없으면 빈 배열
 */
export const optionOutboundNotes = function (fieldSupport, platforms) {
  const rows = (fieldSupport || []).filter(function (row) {
    return row && (!platforms || platforms.length === 0 || platforms.indexOf(row.platform) > -1)
  })
  if (rows.length === 0) return []

  // 같은 답끼리 묶는다 — 키는 「말할 수 있는 축」의 정렬 문자열이다
  const groups = {}
  rows.forEach(function (row) {
    const opened = axisLabels(row.supportedFields)
    const closed = axisLabels(row.unsupportedFields)
    const key = opened.join('|') + '::' + closed.join('|')
    if (!groups[key]) groups[key] = { opened: opened, closed: closed, platforms: [], fields: row.supportedFields || [] }
    groups[key].platforms.push(platformDisplayName(row.platform))
  })

  return Object.keys(groups)
    .map(function (key) {
      const g = groups[key]
      const who = g.platforms.join('·')
      const parts = []
      // **조사를 붙이지 않는다.** 축 라벨은 「…떼기」처럼 받침이 없는 것과 「…추가금액」처럼 있는 것이
      // 섞여 있어 은/는 선택이 필요해진다. 목록을 콜론 뒤로 빼면 그 판정 자체가 사라진다 —
      // 조사 규칙을 문구 유틸이 떠안는 것보다 문장 구조를 바꾸는 편이 낫다.
      if (g.opened.length > 0) parts.push(who + '에 반영되는 것: ' + g.opened.join(' · '))
      if (g.closed.length > 0) {
        parts.push(
          who + '에 반영되지 않는 것: ' + g.closed.join(' · ') + ' — 각 배달앱의 사장님 사이트에서 고쳐 주세요'
        )
      }
      // 구조 조항은 연결 축이 열린 묶음에만 붙는다
      if ((g.fields || []).indexOf('optionGroups') > -1) {
        parts.push(OPTION_NAME_IS_MATCH_KEY)
        parts.push(OPTION_ORDERN_ONLY_FOLDS)
      }
      return parts.join('. ')
    })
    .filter(function (line) {
      return line.length > 0
    })
}

/**
 * **옵션 반영에는 「메뉴 가져오기」 1회가 선행한다 — 다만 이유가 바뀌었다** (§16.198 → §16.199).
 *
 * §16.198 에서 이 문장의 근거는 **지목 재료**였다: 서버가 보내는 것이 플랫폼 옵션그룹 id 의 목록인데
 * 오더엔 옵션 행과 그 id 를 잇는 축이 「메뉴 가져오기」가 적어 둔 관측뿐이었다.
 *
 * §16.199 가 지목을 **이름**으로 옮기면서 그 근거는 사라졌다 — 이름은 오더엔 행만으로 언제나 알 수
 * 있다. **그런데 문장은 살아남는다. 왜 옳은지가 바뀔 뿐이다.** 이제 관측이 답하는 것은 「지목」이
 * 아니라 **「오더엔 구성을 목표로 삼아도 되는가」**다.
 *
 * 인바운드(`MenuOptionLinkWriter.align`)는 그 메뉴의 연결을 전부 지우고 관측 집합을 다시 넣는다.
 * 그래서 **옵션과 함께 한 번이라도 가져왔다면** `오더엔의 현재 구성 = 마지막 관측 + 그 뒤 사장님이
 * 오더엔에서 한 편집` 이 성립하고, 그때 비로소 「오더엔에 없는 그룹」을 **사장님이 뗀 것**으로 읽을
 * 수 있다. 관측이 한 번도 없으면 오더엔 구성은 배달앱과 **무관하게** 만들어진 것일 수 있고(POS 에서
 * 손으로 만든 메뉴, 옵션을 싣지 않던 옛 인바운드), 그 차이를 「뗐다」로 읽으면 **사장님이 배달앱에
 * 붙여 둔 옵션을 우리가 떼게 된다.** 그것이 이 선행 조건의 진짜 이유다.
 *
 * **「누르라」만 말하면 안 된다.** 그 조작은 [OPTION_IMPORT_REALIGNS] 대로 맞추기라 오더엔에서 뗀
 * 옵션을 되붙인다 — 그 사실을 같은 문단에서 말하지 않으면 사장님은 시킨 대로 누르고 지웠던 옵션이
 * 되살아나는 것을 본다. 되붙은 뒤 다시 떼면 그때는 반영되고, **그 마지막 한 문장이** 이 안내가
 * 「그럼 누르지 말아야겠다」로 읽히지 않게 한다.
 */
export const OPTION_OUTBOUND_NEEDS_IMPORT = `옵션 반영은 그 메뉴의 배달앱 옵션을 한 번 읽어 둔 뒤부터 동작합니다 — 읽은 적이 없으면 옵션만 빠진 채 이름·가격이 반영되니, POS 에서 이 가게의 메뉴 가져오기를 1회 실행해 주세요. ${OPTION_IMPORT_REALIGNS} — 되붙은 옵션을 오더엔에서 다시 떼면 그때부터 배달앱에도 반영됩니다`

/**
 * ★ **이 메뉴가 배달앱 여러 곳에 올라가 있다 — 「메뉴 가져오기」가 옵션의 주인을 바꾼다** (§16.201).
 *
 * ## 무엇이 일어나는가
 *
 * 인바운드 `MenuOptionLinkWriter.align` 은 **메뉴 단위**로 `DELETE FROM tbl_smenu_x_smenu_opt
 * WHERE smenu_cd = ?` 를 한 뒤 **지금 가져오는 그 한 배달앱의 관측**만 다시 넣는다. 그래서 한 메뉴가
 * 두 곳 이상에 올라가 있으면 **마지막으로 누른 배달앱의 옵션 구성만 오더엔에 남고** 나머지 배달앱이
 * 만든 옵션 연결은 사라진다. 그 뒤 그 배달앱들의 옵션 아웃바운드는 「오더엔에만 있는 그룹이 붙어
 * 있다」(`ORDERN_ONLY_GROUP`)로 **조용히 접힌다.**
 *
 * ## ★ 이름 문제가 아니다 — 문장이 그렇게 말하면 안 된다
 *
 * 초판 진단은 「3사가 같은 개념을 다른 이름으로 부른다」였고 그것은 **오진이었다**(§16.200 정정).
 * 이름이 3사 전부 같아도 같은 일이 일어난다 — 옵션 **행 집합**이 배달앱마다 다르기 때문이다
 * (실측: 쿠팡 `시럽추가` 에는 배민에 없는 항목 둘이 더 있다). 이름 차이는 오더엔 그룹 행을 둘로
 * 가르는 2차 효과일 뿐이다. 그래서 이 문구는 **이름을 한 글자도 말하지 않는다.**
 *
 * ## 왜 고치지 않고 말하는가
 *
 * 근본 수정은 **「이 링크에 무엇이 있어야 하는가」를 적는 축**이 없이는 성립하지 않는다 — 저장
 * 상태에서 「사장님이 뗐다」와 「다른 배달앱 가져오기가 지웠다」가 **완전히 같기** 때문이다. 설계
 * 3회차가 이름 축·행 축 양쪽으로 시도했다가 전부 그 한 지점에서 깨졌다(§16.201 기록). 그 축을
 * 새로 만드는 것은 기능 하나 크기라 별도 회차이고, **그때까지 화면이 사실을 말하는 것**이 이 상수다.
 *
 * ## 조건부다 — 한 곳에만 올린 매장에게는 없는 걱정이다
 *
 * 배달 링크의 **플랫폼이 둘 이상일 때만** 뜬다. 링크가 없거나 한 곳뿐이면 이 문장은 존재하지 않는
 * 사실이므로 §16.7·§16.177 이 세운 규칙(없는 걱정을 만들지 않는다) 그대로 띄우지 않는다.
 *
 * ## 마지막 문장이 해결책이라야 한다
 *
 * 「멎습니다」로 끝나면 사장님이 할 수 있는 일이 없다. **그 배달앱을 마지막으로 한 번 더 가져오면**
 * 그쪽 구성이 오더엔에 돌아오고 그 배달앱의 반영이 다시 산다 — 참이고, 지금 유일하게 작동하는
 * 해소법이다. 다만 그것이 **번갈아 가며만 된다**는 사실도 같은 문장이 말해야 한다. 그러지 않으면
 * 「한 번 누르면 셋 다 된다」로 읽힌다.
 *
 * ★★ **위 문단 전체가 2026-08-23 에 거짓이 됐다** (규약 §16.219①). 서버가 목표를 **합집합**으로
 * 만들면서 마지막 가져오기가 남의 옵션을 지우지 않는다 — 상수 이름은 참조가 여러 곳이라 그대로
 * 두되 **문장은 바꿨다.**
 *
 * **남는 사실은 방향이 반대다**: 지우는 것이 아니라 **모으는** 쪽이라, 한 배달앱에서만 뗀 옵션이
 * 다른 배달앱에 남아 있으면 오더엔에서는 계속 붙어 있다. 완전히 떼려면 모든 배달앱에서 떼야 한다.
 * 이것은 [OPTION_IMPORT_REALIGNS] 의 되붙기 경고가 배달앱 여러 곳으로 넓어진 것이지 새 예외가
 * 아니다 — 「가져오기는 맞추기이지 되돌리기가 아니다」는 그대로다.
 *
 * pos2-lite 「메뉴 가져오기」 화면의 같은 문장도 함께 바꿨다
 * (`components/settings/DeliveryMenuImportSettings.tsx`). **두 곳이 갈리면 함께 본다.**
 */
export const OPTION_MULTI_PLATFORM_LAST_IMPORT_WINS =
  '이 메뉴는 배달앱 여러 곳에 올라가 있어 각 배달앱의 옵션이 합쳐져 오더엔에 남습니다. 어느 배달앱을 가져와도 다른 배달앱이 붙여 둔 옵션은 그대로 유지되지만, 그래서 한 배달앱에서만 뗀 옵션은 다른 배달앱에 남아 있는 동안 계속 붙어 있습니다 — 완전히 떼려면 모든 배달앱에서 떼고 다시 가져와 주세요'

/**
 * ★★ **내 편집이 남의 매장에도 나간다** — 배민 카탈로그 공유 (`CATALOG-SHARED`).
 *
 * 배민은 메뉴가 **owner 소유**라 한 CatalogMenu 가 여러 샵에 배치되고, 그 샵들이 서로 다른 오더엔
 * 매장에 매핑되면 **한 매장의 편집이 다른 매장의 배민 메뉴를 바꾼다.** dev 실측(2026-08-12):
 * store 13 에서 낸 이름 변경 셋이 전부 성공했고 store 792 의 배민 메뉴 이름이 함께 바뀌었다.
 *
 * 서버 §8 게이트는 이것을 막지 않으며 **막지 않는 것이 맞다** — 오더엔의 사본 전파와 의미가 같다.
 * 그러니 결함은 차단이 아니라 **침묵**이었다: 두 매장 사장님 모두 통보받지 못했다.
 *
 * **문장이 남의 매장을 지목하지 않는다.** 서버가 수만 내리는 것과 같은 이유다 — 남의 사업장
 * 매장코드·상호는 이 화면의 스코프 밖이다. 사장님이 알아야 하는 것은 「함께 바뀐다」는 사실과
 * 「원치 않으면 배민에서 메뉴를 분리해야 한다」는 해소 방향이지 상대가 누구인지가 아니다.
 *
 * 두 매장 **양쪽** 화면에 뜬다 — 다만 **같은 수는 아니다**(§16.224 정정). 「남의 사업장」의 축은
 * **이 계정이 그 매장을 볼 수 있는가**(`AdminStoreScopeResolver.allowsStore`)이지 샵인샵 형제가
 * 아니다 — 리얼에 샵인샵 매장이 0행인데 한 배민 owner 의 카탈로그가 고객 3곳에 걸쳐 있었다.
 * 계정 스코프가 다르면 수도 다르다. 각 화면이 답하는 것은 「내 스코프 밖의 매장이 몇 곳인가」이지
 * 「이 카탈로그를 쓰는 매장 수」가 아니다.
 *
 * ★ 그래서 **전체 스코프 계정(admin·mng)에서는 이 고지가 뜨지 않는다** (§16.226) — 그 계정에는
 * 「남의 매장」이 정의상 없기 때문이다. 운영자용 표현이 필요하면 그때 **다른 문장**을 만든다.
 */
export function catalogSharedNote(sharedStoreCount) {
  if (!(sharedStoreCount > 0)) return ''
  return `이 메뉴는 배민에서 다른 매장 ${sharedStoreCount}곳과 같은 메뉴를 공유합니다. 여기서 이름·가격·품절을 바꾸면 그 매장의 배민 메뉴도 함께 바뀝니다 — 따로 관리하려면 배민 사장님 사이트에서 메뉴를 각각 만들어 분리해 주세요`
}

/**
 * ★ **지목 충돌 — 같은 이름의 옵션그룹이 그 메뉴에 둘 붙어 있다** (§16.199).
 *
 * 이름 키가 새로 만드는 유일한 실패 모드다. id 는 유일했고 이름은 아니다 — 배달앱에서 그 메뉴에
 * 같은 이름의 옵션그룹이 둘 붙어 있으면 오더엔의 그룹 하나가 그중 무엇을 뜻하는지 **고를 근거가
 * 없다.** 하나를 고르면 그 메뉴에서 남의 옵션이 떨어지므로 룰은 그 축만 접고, 이 문장이 그 사실을
 * 사장님에게 전한다.
 *
 * **문장이 셋을 다 말해야 사장님이 고칠 수 있다.**
 *  ① 어느 이름인지 — 화면이 관측에서 파생해 채운다(`{name}`).
 *  ② 무엇을 하라는지 — **배달앱에서** 이름을 바꾼다. 오더엔에서 바꾸면 지목이 더 어긋난다
 *     (오더엔 이름이 목표의 원천이므로, 그쪽을 바꾸면 그 그룹이 배달앱 목록 어디와도 안 맞는다).
 *  ③ 그 뒤에 무엇을 누를지 — 메뉴 가져오기 1회. [OPTION_IMPORT_REALIGNS] 의 되붙기 경고는 이
 *     문단이 이미 [OPTION_OUTBOUND_NEEDS_IMPORT] 와 함께 뜨는 자리라 여기서 다시 적지 않는다.
 *
 * **`result.message` 로 도착하지 않는다** — 그 값은 §16.57 대로 어드민에 표시되지 않는 진단
 * 문자열이다. 이 사실이 화면에 닿는 경로는 **관측 축 한 칸**(`optionAxis.observedNames`)이고,
 * 화면이 거기서 중복을 **파생**해 이 문장을 띄운다(저장하는 것은 관측 하나뿐이라 §2 원칙 3 그대로).
 */
export function optionOutboundAmbiguousNote(names) {
  const quoted = (names || []).map((name) => `‘${name}’`).join(', ')
  return `이 메뉴에 붙은 배달앱 옵션그룹 중 이름이 같은 것이 둘 이상 있어(${quoted}) 어느 쪽을 바꿔야 할지 가릴 수 없습니다. 배달앱 사장님 사이트에서 둘 중 하나의 이름을 다르게 바꿔 주신 뒤, POS 에서 이 가게의 메뉴 가져오기를 1회 실행해 주세요`
}

/**
 * **관측된 옵션그룹 이름 중 둘 이상인 것** — 지목 불가의 술어이자 화면 문구의 재료 (§16.199).
 *
 * 서버는 이 판정을 하지 않는다. **동등 판정의 소유자가 룰 하나여야 하기 때문**이다 — 서버가 자기
 * 동치 관계로 「충돌 없음」이라 하고 룰이 방금 읽은 목록에서 「둘」이라 하면 그 순간 동치 관계가
 * 두 벌이 되고, §16.109 가 collation 에서 겪은 형태가 언어 경계에서 재현된다. 그래서 서버는
 * **사실만 내리고**(`optionAxis.observedNames`) 화면이 여기서 파생한다.
 *
 * 비교는 룰과 같은 규칙이다 — **앞뒤 공백만 뗀다.** 그 이상의 정규화(공백 접기·대소문자)를 여기서
 * 하면 화면이 「충돌」이라 말한 메뉴를 룰이 문제없이 밀거나 그 반대가 된다.
 */
export function duplicateObservedOptionNames(optionAxis) {
  return duplicatedTrimmedNames(optionAxis && optionAxis.observedNames)
}

/**
 * ★★ **오더엔 쪽 이름 중 둘 이상인 것** — 같은 술어, **다른 목록** (규약 §16.228).
 *
 * §16.227⑤ 가 「오더엔에 같은 이름의 옵션그룹이 둘이면 작업을 만들기 전에 접는다」를 세웠는데,
 * 그 사실이 **사장님에게 닿는 통로가 없었다** — 서버의 `OptionFoldReason` 은 프로덕션에서 아무도
 * 읽지 않고(write-only), 화면의 중복 판정은 **플랫폼 쪽**(`observedNames`)만 봤다.
 *
 * 해법 방향이 반대라 [optionOutboundAmbiguousNote] 를 재사용하지 않는다 — 그쪽은 「배달앱에서
 * 이름을 바꾸세요」이고 이쪽은 **「오더엔에서 바꾸세요」**다.
 */
export function duplicateOrdernOptionNames(optionAxis) {
  return duplicatedTrimmedNames(optionAxis && optionAxis.ordernNames)
}

/**
 * 비교는 룰·서버와 같은 규칙이다 — **앞뒤 공백만 뗀다.** 그 이상의 정규화(공백 접기·대소문자)를
 * 여기서 하면 화면이 「충돌」이라 말한 메뉴를 룰이 문제없이 밀거나 그 반대가 된다.
 */
function duplicatedTrimmedNames(names) {
  const seen = Object.create(null)
  const duplicated = []
  for (const raw of names || []) {
    const name = String(raw == null ? '' : raw).trim()
    if (!name) continue
    if (seen[name] === true) {
      if (duplicated.indexOf(name) < 0) duplicated.push(name)
      continue
    }
    seen[name] = true
  }
  return duplicated
}

/**
 * ★ **오더엔 쪽 동명 그룹의 안내** (§16.228).
 *
 * 지목 키는 **그룹 이름**인데(§16.199) 오더엔은 같은 이름의 옵션그룹을 다른 행으로 허용한다.
 * 그러면 두 오더엔 그룹이 같은 배달앱 그룹을 가리켜 각자 작업을 만들고, 마지막 실행이 앞선 것을
 * 덮는다. 그래서 서버가 **작업을 만들기 전에** 그 축을 접고, 이 문장이 그 사실과 해소 방향을 전한다.
 */
/**
 * ★ **이 메뉴에 옵션 축이 하나라도 열려 있는가** — capability 파생 (§16.229).
 *
 * §16.214 가 옵션 안내를 상수에서 `fieldSupport` 파생으로 바꾸며 세운 규칙이다(`OPTION-NOTICE-
 * CAPABILITY-COUPLING`). 그 규칙을 지키지 않으면 **아무것도 동기화되지 않는 축**에 대해
 * 사장님에게 조치를 요구하게 된다 — 리얼 실측(2026-08-24) capability 18행에 옵션 행이 하나도
 * 없고, §16.228① 이 지키려던 긴급 중지(`active_yn='N'`)를 실제로 걸면 dev 도 같은 상태가 된다.
 */
export function hasAnyOptionAxisOpen(fieldSupport) {
  return (fieldSupport || []).some(function (row) {
    return (
      row &&
      (row.supportedFields || []).some(function (field) {
        return OPTION_AXIS_LABEL[field] !== undefined
      })
    )
  })
}

export function optionOrdernAmbiguousNote(optionAxis) {
  const duplicated = duplicateOrdernOptionNames(optionAxis)
  if (duplicated.length === 0) return ''
  const quoted = duplicated.map((name) => `"${name}"`).join(', ')
  return `오더엔에 이름이 같은 옵션그룹이 둘 이상 있어(${quoted}) 배달앱의 어느 그룹을 가리키는지 가릴 수 없습니다. 오더엔에서 둘 중 하나의 이름을 다르게 바꿔 주세요`
}

/**
 * 그 필드에 붙는 부기 — 포장 가격은 그 플랫폼의 반영 조건, 사용 여부는 그 플랫폼에서의 숨김 범위.
 * 없으면 `null` — 모든 필드가 설명을 갖는 것은 아니고, 플랫폼마다 갈리는 부기는 플랫폼을 모르면 답하지 않는다.
 *
 * @param {string} field 필드 코드
 * @param {?string} platform 그 행의 플랫폼 코드(`BAEMIN`·`YOGIYO`·`COUPANG_EATS`·`DDANGYO`)
 */
export function deliveryFieldNote(field, platform) {
  return platformNoteOf(FIELD_PLATFORM_NOTES[field], platform)
}

/**
 * 그 채널 목표에 붙는 조건 안내 — 조건 문장은 `platforms`(그 화면에 그 채널 입력칸이 열린 플랫폼, 표시 순서)의
 * 것만 잇고 같은 문장은 한 번만 적는다. 이을 문장이 없으면(배달 채널 · 조건 없는 플랫폼뿐) `null`.
 */
export function deliveryChannelPendingNote(channel, platforms) {
  const byPlatform = CHANNEL_PLATFORM_CONDITIONS[channel]
  const conditions = (platforms || [])
    .map((platform) => platformNoteOf(byPlatform, platform))
    .filter((condition, index, all) => !!condition && all.indexOf(condition) === index)
  return conditions.length > 0 ? `${CHANNEL_PENDING_HEAD} — ${conditions.join('. ')}` : null
}

/**
 * 품절의 **기한** — 그것은 **그 배달앱의 기본값**이지 우리가 정한 값이 아니다 (규약 §16.243).
 *
 * 이 문장 없이 「품절 연동」이라 부르면 사장님은 기한이 지나 되살아난 것을 우리 결함으로 읽는다.
 *
 * ★★ **시각을 말하지 않는다.** 종전 문장은 「다음날 아침 · 배민 08시 · 요기요 10시」였는데
 * 배민에서 **09시도 관측됐다**(2026-08-25). 애초에 성립할 수 없는 문장이었다 — 3사 여섯 개
 * 쓰기 경로 어디에도 **만료 시각 파라미터가 없어** 해제 시각은 전부 플랫폼이 정한다. 우리는 그
 * 시각을 보내지도, 저장하지도(`observed_snapshot.stopSelling` 은 불리언), 알지도 못한다.
 *
 * 이름이 `OPTION_` 으로 시작하지 않는 것도 그래서다 — **메뉴 축과 옵션 축이 같은 사실**이다
 * (§16.232⑥·§16.243⑤). 축이 아니라 사실이 이 상수의 주인이다.
 */
export const SOLD_OUT_PLATFORM_DEFAULT =
  '배달앱에는 그 앱의 기본 품절로 나갑니다 — 배달의민족 「품절」, 요기요 「하루품절」, 쿠팡이츠 「오늘만 품절」과 같습니다. 기한이 지나면 배달앱이 스스로 판매를 재개하며 그 시각은 앱마다 다릅니다. 계속 품절로 두시려면 배달앱에서 품절 기간을 확인해 주세요'

/**
 * 배달앱이 반영을 거절했는데 **사유를 모를 때**의 다음 걸음 (미해결 `EXEC-REJECTION-REASON-LOST`).
 *
 * ★★ **사유를 지어내지 않는다.** 서버는 사유를 모를 때 `resultMessage` 를 `null` 로 내린다 —
 * *「서버가 모르는 것을 아는 척하지 않는다」*가 그쪽의 규칙이다. 그 자리에서 화면이 가장 구체적인
 * 사유를 단정하면 그 규칙을 화면이 대신 깨는 셈이 된다.
 *
 * 초판은 이 문구가 *「이 **값**을 받지 않았습니다. **값을 조금 바꿔** 다시 시도…」*였는데,
 * `PLATFORM_REJECTED` 는 **다섯 사유를 한 칸에 담는다** — 값 거절 · 요청 제한(429) · 플랫폼 오류
 * (5xx) · 권한 없음 · 그리고 **사유 축을 아예 싣지 않는 옛 단말**. 룰·단말·서버가 각자 배포되므로
 * 마지막 경우가 단말이 갱신될 때까지의 **전 기간 다수**다. 429 는 값이 평가조차 되지 않았고,
 * 5xx 는 서버 KDoc 이 *「플랫폼이 반영한 뒤 응답만 실패한 경우를 포함한다」*고 적은 갈래다 —
 * 거기서 「값을 바꿔 다시 저장하라」는 **원하지 않은 값을 배달앱으로 내보내는 안내**다.
 *
 * 사유별 문장의 소유자는 서버(`DeliveryMenuRejectionReason.messageOf`) 하나다. 그 문장이 오면
 * [FAILED_CHIP] 의 `note` 규칙에 따라 이 정적 문구를 덮는다.
 */
export const PLATFORM_REJECTED_ACTION =
  '배달앱이 이 저장을 받아들이지 않았습니다. 배달앱에서 값이 바뀌었는지 확인하신 뒤 다시 저장하시거나 배달앱에서 직접 수정해 주세요'

/**
 * **같은 이름 그룹이 둘 이상이면 저장이 그 전부에 번진다** (미해결 `OPTION-NAME-FANOUT-NOTICE`).
 *
 * 지목 키가 이름이라(§16.199) 이것은 결함이 아니라 **설계**다 — 그런데 dev 실측에서 배민에
 * `샷추가` 가 둘이었고 한 번의 push 로 **둘 다** 최대 1개가 됐다. 사장님이 둘을 다르게 쓰고
 * 있었다면 의도치 않은 변경이고, 그 사실을 말할 자리가 화면에 없었다.
 *
 * **수는 서버가 센다** — 화면이 관측을 다시 세면 그 사본이 판정과 갈린다(§16.214).
 */
/**
 * ★★★ **이 그룹을 다른 매장도 함께 쓴다** (`OPTION-CROSS-STORE-CONFLICT`).
 *
 * 배민 owner 하나 아래 여러 오더엔 매장이 **같은 플랫폼 옵션그룹**을 지목할 수 있다. 여기서
 * 값을 바꾸면 **그 매장들의 배달앱도 함께 바뀐다** — 사장님이 「내가 안 누른 변경이 내
 * 배달앱에 나타난다」를 겪는 자리이고, 오늘까지 화면은 그 사실을 말한 적이 없었다.
 *
 * ★ **막지 않는다.** 막으면 평범한 단일 매장 토글이 상시 죽는다(§16.235② 가 8/25 에 되돌린
 * 그 갈래) — 말하는 것이 이 축의 답이다.
 *
 * 서버가 `null` 을 내리면(요기요·쿠팡이거나 관측이 없으면) 이 문장을 쓰지 않는다.
 */
export function deliveryOptionGroupSharedStoreNote(platformLabel, sharedStoreCount) {
  return `${platformLabel}의 이 옵션 그룹은 다른 매장 ${sharedStoreCount}곳도 함께 씁니다. 여기서 바꾸면 그 매장들의 배달앱에도 같이 반영됩니다.`
}

export function deliveryOptionGroupFanoutNote(platformLabel, groupCount) {
  return `${platformLabel}에 이름이 같은 옵션 그룹이 ${groupCount}개 있어 이 저장이 그 ${groupCount}개 전부에 반영됩니다`
}

/**
 * **최대 선택 개수의 상한** (미해결 `OPTION-SELECT-COUNT-UNPUSHABLE`).
 *
 * 배민은 노출 옵션 수보다 큰 최대값을 **원자적으로 거절**한다(dev 실측: 옵션 2개 그룹에
 * `max 3` → `PLATFORM_REJECTED`). 그 상한은 **가장 좁은 동명 그룹**이 정하므로 —
 * 오더엔이 지목한 적 없는 남의 그룹이 거절을 만들 수 있다 — 사장님이 자기 그룹만 봐서는
 * 알 수 없다. 2026-08-25 에 네 값으로 전수 재현했고 예외가 없었다.
 *
 * ★ **관측이 없으면 이 문장을 쓰지 않는다** — 서버가 `null` 을 내리고, 그때는 상한을
 * 「모르는」 것이지 「없는」 것이 아니다.
 */
export function deliveryOptionSelectCountCapNote(maxSelectableCount) {
  return `배달앱에는 최대 선택 개수가 ${maxSelectableCount}까지만 반영됩니다 — 배달앱이 옵션 수보다 큰 값을 거절합니다. 더 큰 값은 오더엔(POS·QR)에만 적용됩니다`
}

/**
 * **오더엔에만 있는 옵션 항목** — 그 이름은 배달앱으로 나가지 않는다
 * (미해결 `OPT-ITEM-ADD-NOT-PUSHED`).
 *
 * 항목 축의 목표는 **관측에 있는 이름**으로 좁혀지므로(§16.227) 새로 더한 항목은 목표에서 빠진다.
 * 설계상 옳지만 **아무도 말하지 않았다** — 그룹에 대해서는 `ORDERN_ONLY_GROUP` 이 말해 주는데
 * 항목에는 그 자리가 없어, 사장님이 옵션을 추가하고 **반영을 기다렸다**(dev 실측 `테스트 샷`).
 *
 * 그룹판 문구 옆에 붙이는 문장이라 **같은 모양**으로 쓴다 — 「없다 · 배달앱에서 만들어 달라」.
 */
/**
 * ★★★ **이 관측이 얼마나 오래된 것인가** (`OPTION-OBSERVE-LAG`).
 *
 * 플랫폼에서 사장님이 방금 바꾼 직후 밀면 **관측이 옛 상태**라 지목에 실패해 닫힌다
 * (dev 실측 990404 — 재연결 직후 push). 다음 관측 회차에 저절로 풀리지만, 화면이 아무 말도
 * 하지 않으면 사장님은 **왜 안 되는지 모른 채 같은 조작을 반복**한다.
 *
 * ## 임계값을 여기 두지 않는다
 *
 * 관측 주기는 **실행자(pos2-lite)가 소유**한다. 「N분보다 오래됐으면 낡음」을 화면에 적으면 그
 * 상수가 두 벌이 되고, 주기가 바뀌는 날 화면만 거짓말을 한다. 그래서 판정하지 않고 **사실만
 * 말한다** — 나이와, 그 나이가 뜻하는 것.
 *
 * @param {string|Date|null} observedAt 관측 시각
 * @returns {string|null} 안내 문구. 관측이 없으면 `null`
 */
export function deliveryObservationAgeNote(observedAt) {
  const age = deliveryObservationAgeText(observedAt)
  if (age === null) return null
  return `이 값은 ${age} 읽은 배달앱 상태입니다. 방금 배달앱에서 직접 바꾸셨다면 다음 관측이 올 때까지 반영이 실패할 수 있습니다.`
}

/**
 * 관측 **나이** 한 토막 — 위 문장과 POS 임베드 화면(관측 시각 열·머리 통계)이 같은 표현을 쓴다 (§16.278).
 *
 * 임계값을 두지 않는다 — 판정하지 않고 나이만 말한다(위 주석의 이유 그대로). 1분 미만은 `방금`, 하루가 넘으면
 * 일 단위로 접는다. 미래 시각(시계 어긋남)도 `방금` 이다 — 그 값이 "관측이 없다"로 읽히면 안 된다.
 *
 * @param {string|Date|null} observedAt 관측 시각
 * @param {number} [now] 기준 시각(ms) — 테스트가 주입한다. 없으면 현재
 * @returns {string|null} `방금`·`n분 전`·`n시간 n분 전`·`n일 전`. 관측이 없거나 읽을 수 없으면 `null`
 */
export function deliveryObservationAgeText(observedAt, now) {
  if (!observedAt) return null
  const at = observedAt instanceof Date ? observedAt : new Date(observedAt)
  if (Number.isNaN(at.getTime())) return null
  const base = typeof now === 'number' ? now : Date.now()
  const minutes = Math.floor((base - at.getTime()) / 60000)
  if (minutes < 1) return '방금'
  if (minutes < 60) return `${minutes}분 전`
  if (minutes < 60 * 24) return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분 전`
  return `${Math.floor(minutes / (60 * 24))}일 전`
}

export function deliveryOrdernOnlyItemsNote(names) {
  return `${names.join(
    ', '
  )}의 연결 대상을 확인하지 못했습니다. 옵션 연결에서 메뉴·옵션그룹과 배달앱 목록을 비교해 주세요. 이 표시만으로 배달앱에 옵션이 없다고 판단할 수 없습니다.`
}

/**
 * 옵션 품절이 **그 배달앱에 반영되지 않았다**는 고지 (규약 §16.203).
 *
 * 옵션 연결 축에서 접힘은 무해했지만(아무것도 붙이지도 떼지도 않는다) 품절 축에서 접힘은
 * **「품절인데 판다」**이다. 안전 방향이 반대이므로 조용히 넘기지 않는다.
 */
export const OPTION_SOLD_OUT_NOT_APPLIED =
  '이 배달앱에는 옵션 품절이 반영되지 않았습니다 — 배달앱에 그 옵션이 없거나, 「메뉴 가져오기」를 아직 실행하지 않았습니다. 손님이 품절된 옵션을 주문할 수 있으니 배달앱에서 직접 품절 처리해 주세요'

/**
 * 옵션 사용 여부(숨김·해제)가 **그 배달앱에 반영되지 않았다**는 고지 (규약 §16.302).
 *
 * 품절 축([OPTION_SOLD_OUT_NOT_APPLIED])과 **갈라 둔 문장**이다 — 접힘의 결과가 방향에 따라 다르다.
 * 지운 옵션이 접히면 「지웠는데 판다」(손님이 주문한다)이고, 다시 켠 옵션이 접히면 「켰는데 안 판다」
 * (배달앱에서는 여전히 숨김)라 사장님이 할 일이 반대다. 한 문장에 접으면 둘 중 하나가 거짓이 된다.
 */
export const OPTION_USE_YN_NOT_APPLIED =
  '이 배달앱에는 옵션 사용 여부(숨김·해제)가 반영되지 않았습니다 — 배달앱에 그 옵션이 없거나, 「메뉴 가져오기」를 아직 실행하지 않았습니다. 지운 옵션이면 손님이 계속 주문할 수 있고, 다시 켠 옵션이면 배달앱에서는 아직 숨김이니 배달앱에서 직접 맞춰 주세요'

// ────────────────────────────────────────────────────────────────────
// POS 임베드 「배달 메뉴·옵션 품절 관리」 화면 (결정 브리프 2026-09-03 · D8)
// 라우트 `/v/pos/delivery-soldout` · 화면 `views/pos/PosDeliverySoldoutView.vue` · 판정 `utils/posDeliverySoldout.js`
// ────────────────────────────────────────────────────────────────────

/**
 * 옵션 항목 지목 결과(`match`)의 chip 낱말 — 표는 `deliveryMenuSync.deliveryOptionChip` 이 접는다.
 *
 * `GET /api/delivery-menu/option-status` 의 `match` 는 옵션 항목을 관측 그래프에서 **이름으로** 찾은 결과다
 * (§16.203 의 `UNKNOWN_PLATFORM_ITEM`·`AMBIGUOUS_OPTION_ITEM` 과 대응). 없으면 배달앱에서 만들어야 하고, 같은 이름이
 * 둘이면 배달앱에서 이름을 갈라야 한다 — 둘 다 이 화면이 실행하지 못하는 상태라 chip 이 그 사실을 말한다.
 * 없음의 부기는 [deliveryOrdernOnlyItemsNote] 와 같은 문장이다(같은 사실).
 */
export const OPTION_MATCH_MISSING_TEXT = '옵션 연결 확인 필요'
/**
 * ★ **「읽었는데 없음」과 「아직 못 읽음」은 다른 사실이다** (2026-09-03 리뷰 지적).
 *
 * 서버의 `match:"MISSING"` 은 둘 다에 쓰이고 `observed == null` 이 후자를 가른다(§16.304②).
 * 관측이 아직 없는데 「배달앱에 이 옵션 없음 — 배달앱에서 먼저 만들어 주세요」라고 말하면 **거짓 안내**다:
 * 리얼은 옵션 축 관측이 아직 한 번도 돌지 않아 `observed_option_groups` 가 전부 비어 있으므로, 가르지 않으면
 * 연동된 모든 매장의 **모든 옵션**이 그 거짓 문장을 받는다. 낱말은 링크 없음 축이 이미 쓰는 것을 그대로 쓴다
 * (`unlinkedReason==='NEVER_IMPORTED'` → 「메뉴 가져오기 필요」) — 사장님이 할 일이 같으므로 어휘도 같아야 한다.
 */
export const IMPORT_REQUIRED_TEXT = '메뉴 가져오기 필요'
/** 옵션 축이 쓰는 이름 — 낱말의 소유자는 [IMPORT_REQUIRED_TEXT] 하나다 */
export const OPTION_MATCH_UNREAD_TEXT = IMPORT_REQUIRED_TEXT
export const OPTION_MATCH_AMBIGUOUS_TEXT = '옵션 연결 확인 필요'
export const OPTION_ITEM_AMBIGUOUS_NOTE =
  '연결 대상이 여러 개이거나 연결 정보가 바뀌었습니다. 옵션 연결에서 메뉴·옵션그룹·추가금액과 현재 대상을 비교해 주세요.'

export const OPTION_MAPPING_TEXT = Object.freeze({
  OVERVIEW:
    '메뉴·옵션그룹·추가금액을 비교한 뒤 연결 대상을 확정하세요. 연결 없음은 배달앱에 옵션이 없다는 뜻이 아닙니다.',
  EMPTY: '수집된 배달앱 메뉴가 없습니다. POS에서 메뉴 가져오기를 실행한 뒤 다시 조회해 주세요.',
  UNREAD: '이 메뉴의 배달앱 옵션을 아직 읽지 못했습니다. POS에서 메뉴 가져오기를 실행한 뒤 다시 조회해 주세요.',
  NO_CANDIDATE: '조회된 옵션이 없습니다. 검색어와 최신 배달앱 수집 정보를 확인해 주세요.',
  READ_ONLY: '조회 권한만 있습니다. 연결 변경은 권한이 있는 담당자에게 요청해 주세요.',
  SCOPE: '선택한 메뉴·플랫폼의 오더엔 옵션 연결 1건을 변경합니다. 다른 연결은 유지합니다.',
  OUTBOUND:
    '수동 연결 후 품절·판매 재개 요청은 선택한 배달앱 옵션을 대상으로 처리합니다. 같은 배달앱 옵션을 공유하는 메뉴에도 상태가 적용됩니다. 이름·추가금액·숨김·선택 개수 변경은 지원 범위에 포함되지 않습니다.',
  SAVE_EFFECT: '연결 저장으로 옵션명·가격·품절 상태를 변경하거나 과거 실패 요청을 다시 실행하지 않습니다.',
  SAVED: '연결 저장 성공. 배달앱 값 변경은 요청하지 않았습니다.',
  SAVED_REFRESH_FAILED: '연결 저장은 성공했으나 재조회에 실패했습니다. 다시 조회해 실제 연결을 확인해 주세요.',
  SAVE_UNCERTAIN: '저장 결과를 확인하지 못했습니다. 다시 조회해 실제 연결을 확인해 주세요.',
  LOAD_FAILED: '조회에 실패했습니다. 다시 조회해 주세요.',
  REFRESH_FAILED: '현재 연결 재조회도 실패했습니다.',
})

/**
 * 화면 골격의 문장 — 안내·확인창·진행 드로어·토스트.
 *
 * 시안(v6)의 상태 8종 어휘(success/processing/failure/unread/off/none/mapping/unsupported)는 쓰지 않는다 — 플랫폼
 * 상태는 `deliveryMenuChip`·`deliveryOptionChip` 이 `ITEM_STATUS`·`LINK_STATE`·`unlinkedReason` 에서 만든다. 시안의
 * 「아직 못 읽음」은 관측 나이([deliveryObservationAgeText])가 대신한다(§16.278 — 임계값 없음).
 */
export const POS_SOLDOUT_TEXT = Object.freeze({
  TITLE: '배달 메뉴·옵션 품절 관리',
  SUBTITLE_MENU: '오더엔 메뉴 상태를 기준으로 연결된 배달앱에 품절·판매 재개를 요청합니다.',
  SUBTITLE_OPTION: '오더엔 옵션 상태를 기준으로 연결된 배달앱에 품절·판매 재개를 요청합니다.',
  NOTICE_MENU:
    '오더엔 목표 상태와 배달앱에서 마지막으로 읽은 상태를 구분해 표시합니다. 배달앱이 스스로 판매를 재개해도 오더엔 품절은 남을 수 있으니 관측 시각을 함께 확인하세요.',
  NOTICE_OPTION:
    '옵션 목표 상태와 배달앱에서 마지막으로 읽은 상태를 구분해 표시합니다. 배달앱에서 옵션을 정확히 찾지 못한 항목도 오더엔에는 저장되며, 배달앱 반영 여부는 확인창이 말합니다.',
  PLATFORM_FILTER_NOTE: '플랫폼 보기는 목록만 좁히며 반영 대상을 고르지 않습니다.',
  // 페이지 안 안내 — 임베드 라우트는 리다이렉트하지 않는다 (D1·D2)
  NOT_LOGGED_IN: '로그인 정보가 없습니다. POS 에서 다시 열어 주세요.',
  NO_PERMISSION: '이 계정에는 배달 메뉴 조회 권한이 없습니다. 오더엔 관리자에게 문의해 주세요.',
  STORE_MISSING: '매장을 확인할 수 없습니다. POS 에서 다시 열어 주세요.',
  /**
   * 같은 사실의 어드민 진입판 (2026-09-05 D-S1) — 사이드 메뉴로 들어온 창에 「POS 에서 다시 열어 주세요」는 갈 곳이
   * 없는 안내다. 어드민에서는 쿼리가 없으면 세션 매장으로 여는데(D-S2), 그 계정에 매장이 없으면 열 매장 자체가 없다.
   */
  STORE_MISSING_ADMIN: '매장을 확인할 수 없습니다. 이 계정에는 기본 매장이 없습니다.',
  /** 매장 축이 아직 안 정해졌다 — 「확인할 수 없습니다」로 깜빡이지 않는다 (D-S2) */
  STORE_RESOLVING: '매장 정보를 확인하는 중입니다…',
  STORE_OUT_OF_SCOPE: '매장을 확인할 수 없습니다. 이 계정으로 볼 수 없는 매장입니다.',
  LOAD_FORBIDDEN:
    '이 매장의 정보를 읽을 수 없습니다. 권한이 없거나 로그인이 끝났을 수 있습니다. POS 에서 다시 열어 주세요.',
  LOAD_FAILED: '목록을 불러오지 못했습니다.',
  /** 목록은 왔는데 반영 상태만 못 읽었다 — 「목록을 못 불러왔다」고 말하면 눈앞의 목록과 어긋난다 */
  STATUS_LOAD_FAILED: '배달앱 반영 상태를 읽지 못했습니다. 상태를 모르는 동안에는 저장할 수 없습니다.',
  OPTION_LOAD_FAILED: '옵션 상태를 불러오지 못했습니다.',
  ADMIN_ONLY_ACTION: '이 작업은 오더엔 관리자 화면에서 할 수 있습니다.',
  // 행
  LINK_REQUIRED: '연결 필요',
  /**
   * 지목하지 못한 항목이 섞였다 — **나가지 않는다는 뜻이 아니다** (2026-09-03 리뷰 · 규약 §16.304②).
   * 옵션 쓰기의 실행 대상은 그룹이라, 항목을 못 지목해도 링크가 있으면 그룹 값이 통째로 나간다.
   */
  OPTION_NOT_POINTED:
    '배달앱에서 정확히 찾지 못한 항목이 섞여 있습니다 — 반영은 옵션그룹 단위라 그 그룹이 오더엔 값대로 맞춰집니다.',
  NO_LINK: '배달앱 연결 없음',
  STATUS_LOADING: '상태를 읽는 중',
  /** 읽지 못했다 — 「읽는 중」이라고 계속 말하면 거짓이 된다 (2026-09-03 리뷰) */
  STATUS_UNREAD: '상태를 읽지 못함',
  /** 품절 축이 갈라졌다 — 마지막 작업이 성공이어도 지금 값은 다르다 (2026-09-03 리뷰) */
  SOLD_OUT_DRIFT: '배달앱 품절 값 다름',
  OBSERVED_SOLD_OUT: '배달앱 관측: 품절',
  OBSERVED_SELLING: '배달앱 관측: 판매중',
  OBSERVED_HIDDEN: '배달앱 관측: 숨김',
  OBSERVED_UNKNOWN: '배달앱 관측: 모름',
  /**
   * 매장 셀렉터 (2026-09-05 D-S3′) — 부제 줄의 첫 자리다. 배지는 **형제 매장일 때만** 붙인다(기본 상태에 배지를
   * 달면 좁은 줄만 먹는다). 두 진입점 모두 그린다 — 임베드 동선에는 pos2-lite 의 매장 스위처가 없다.
   */
  STORE_BADGE_SIBLING: '형제 매장',
  STORE_SWITCH_BUSY: '저장·반영 확인이 끝난 뒤에 매장을 바꿀 수 있습니다.',
  /** 형제 목록을 **못 읽은** 것 — 「형제가 없다」와 같은 말이 아니다(13R P30). 모른다고 말해야 다시 열어 본다 */
  STORE_SIBLINGS_UNREAD: '함께 쓰는 매장 목록을 읽지 못했습니다.',
  // 확인창 (D5·D6)
  SCOPE_NOT_PER_PLATFORM:
    '플랫폼을 개별로 고르는 작업이 아닙니다. 연결·정책·capability 상태에 따라 함께 요청되거나 제외됩니다.',
  BAEMIN_SHARED_GENERAL: '배민에서 다른 오더엔 매장과 메뉴를 공유하는 건은 그 매장의 배민 메뉴도 함께 바뀝니다.',
  OPTION_GROUP_SCOPE:
    '옵션 품절·판매 재개는 옵션그룹 단위로 나가 같은 그룹의 다른 옵션도 오더엔의 품절 상태대로 맞춰집니다.',
  NO_DELIVERY_TARGET: '배달앱 반영 대상 없음 — 오더엔에만 저장됩니다.',
  NOTHING_SELECTABLE: '선택한 항목 중 실행할 수 있는 것이 없습니다.',
  /**
   * 확인한 집합과 보내기 직전의 집합이 다르다 (11R P1) — 확인창이 열린 사이 폴링이 목록을 갈아 끼워 어떤 행이 진행 중·
   * 방금 보냄·상태 모름이 됐다. 그때는 보내지 않는다(옛 행으로 스냅샷을 찍으면 판정이 거짓이 된다).
   */
  TARGETS_CHANGED: '상태가 바뀌어 보내지 않았습니다. 다시 확인해 주세요.',
  /**
   * 대표 작업의 원천 매장이 이 계정의 스코프 밖이다 (11R P6 · 규약 §16.304② (c‴)) — 후보·잠금·chip 은 사업장 축의 사실
   * 그대로지만 세부(함께 반영되는 가게·사유)는 계정 스코프라 서버가 비워 내리고(`originVisible:false`), 화면은 이 한 줄만.
   */
  ORIGIN_OTHER_STORE: '같은 사업장의 다른 매장이 보낸 작업입니다.',
  /**
   * 확인창의 형제 매장 경고 (2026-09-05 D-S6) — **마지막 방어선**이다. 어드민에서 매장을 바꿔 놓고 잊은 채 품절을
   * 누르면 그 형제 매장의 배달앱에 그대로 나간다. 확인창이 대상 매장 이름을 말하고, 내 매장이 아니면 그 사실도 말한다.
   */
  CONFIRM_SIBLING_STORE: '내 매장이 아닌 형제 매장입니다 — 이 매장의 배달앱에 나갑니다.',
  // 진행 드로어 (D5)
  PROGRESS_TITLE: '배달앱 반영 상태',
  PROGRESS_SAVING: '오더엔에 저장하는 중입니다.',
  PROGRESS_POLLING: '배달앱 응답을 확인하는 중입니다.',
  PROGRESS_ALL_DONE: '모든 반영 대상의 확인을 마쳤습니다.',
  /**
   * 확인을 멈춘 자리 — **아래 상태가 지금 상태라고 말하지 않는다** (2026-09-03 실측 정정).
   *
   * 종전 문구는 「목록을 새로고침해 확인해 주세요」였는데, 그동안 드로어는 멈춘 순간의 칩을 **현재처럼** 계속
   * 보여 줬다. POS 가 붙어 반영이 끝난 뒤에도 「POS 연결 대기」가 그대로 남아 화면이 거짓을 말했다.
   */
  PROGRESS_TIMEOUT: '확인을 멈췄습니다. 아래는 마지막으로 확인한 시각의 상태입니다.',
  PROGRESS_TIMEOUT_NOTE:
    '배달앱 반영은 POS 가 순서대로 처리하며 몇 분이 걸릴 수 있습니다. 「다시 확인」을 누르거나 목록에서 상태를 보실 수 있습니다.',
  PROGRESS_RECHECK: '다시 확인',
  PROGRESS_WAITING: '반영 요청 확인 중',
  PROGRESS_NOT_TARGET: '이번 요청 대상 아님',
  SAVE_FAILED: '저장 실패',
  SAVE_DONE: '저장 완료',
  REAPPLY_BUTTON: '현재 값 다시 저장',
  REAPPLY_NOTE: '같은 값을 다시 저장해 실패한 대상에 새 반영 요청을 만듭니다.',
  /**
   * D7 — appserver 검증 완료(2026-09-03). 계획기는 관측이 목표와 같은 플랫폼에 작업을 내지 않고
   * `ALREADY_IN_SYNC` 로 닫는다. 다만 같은 실행 대상에 **끝나지 않은 다른 반영**이 있으면 증명할 수 없어
   * 한 번 더 나간다(규약 §16.303). 그 예외까지가 이 화면이 단정할 수 있는 폭이다.
   */
  REAPPLY_SKIPS_DONE:
    '이미 반영된 배달앱에는 다시 나가지 않지만, 진행 중인 다른 반영이 있으면 한 번 더 나갈 수 있습니다.',
  /** 재저장으로 풀리지 않는 축 — 답은 어드민의 「값 차이 해결」이고 POS 안에서는 이동시키지 않는다 (D7) */
  BLOCKED_TITLE: '배달앱에서 값이 달라져 반영이 멈춘 대상이 있습니다.',
  BLOCKED_NOTE: '이 대상은 다시 저장해도 나가지 않습니다. 관리자 페이지의 「값 차이 해결」에서 먼저 정리해 주세요.',
  // 토스트
  SAVED_CHECKING: '저장을 마쳤습니다. 배달앱 반영 상태를 확인합니다.',
  OBSERVE_REQUESTED: '관측 요청을 전달했습니다. 잠시 후 「목록 새로고침」으로 확인해 주세요.',
  OBSERVE_FAILED: '관측 요청을 전달하지 못했습니다. POS에서 화면을 다시 열어 주세요.',
  LIST_REFRESHED: '목록을 다시 읽었습니다.',
})

/** 이미 같은 목표 상태라 제외한 건수 — 확인창을 열기 전에 토스트로 말한다 (D5) */
export function posSoldoutSkippedNote(count, stopSelling) {
  return `이미 ${stopSelling ? '품절' : '판매중'}인 ${count}건은 제외했습니다.`
}

/** 배달앱 반영 대상이 없는 건은 오더엔에만 저장된다 — 확인창 한 줄 */
export function posSoldoutNoTargetNote(count, kindLabel) {
  return `배달앱 반영 대상이 없는 ${kindLabel} ${count}건은 오더엔에만 저장됩니다.`
}

/** 배민 카탈로그 공유 — 그 메뉴 줄의 부기. `sharedStoreCount` 는 배민만 유효하다 (D8) */
export function posSoldoutSharedStoreCaption(count) {
  return `배민 · 다른 오더엔 매장 ${count}곳과 공유`
}

/** 진행 드로어 실패 카드 제목 — 플랫폼 실패와 오더엔 저장 실패를 따로 센다 */
export function posSoldoutFailureTitle(failedPlatformCount, saveFailedCount) {
  const parts = []
  if (failedPlatformCount > 0) parts.push(`${failedPlatformCount}개 플랫폼을 반영하지 못했습니다`)
  if (saveFailedCount > 0) parts.push(`${saveFailedCount}건은 오더엔 저장에 실패했습니다`)
  return parts.length > 0 ? parts.join(' · ') + '.' : ''
}

/** 저장 진행 `i/n` */
export function posSoldoutSavingText(index, total) {
  return `저장 중 ${index}/${total}`
}

/** 확인창 제목·부제·미리보기 제목 — 건수와 종류(메뉴·옵션)만 다르다 */
export function posSoldoutConfirmTitle(count, kindLabel, stopSelling) {
  return `선택한 ${kindLabel} ${count}개를 ${stopSelling ? '품절 처리' : '판매 재개'}할까요?`
}

export function posSoldoutConfirmSubtitle(count, kindLabel) {
  return `${kindLabel} ${count}개가 연결된 배달앱 범위로 함께 요청됩니다.`
}

export function posSoldoutPreviewTitle(count, kindLabel) {
  return `처리할 ${kindLabel} ${count}개`
}

/** 확인창의 대상 매장 한 줄 (2026-09-05 D-S6) — 이름을 말하는 자리다. 형제 매장이라는 사실은 경고 줄이 덧붙는다 */
export function posSoldoutConfirmStoreLine(storeName) {
  return `대상 매장 · ${storeName}`
}

/**
 * 매장 전환 목록의 한 줄 (2026-09-05 D-S3′).
 *
 * **표시는 내 매장 쪽에 붙인다.** 형제 쪽에 「(형제)」를 붙이면 목록의 대다수에 접미가 달리고, 매장 이름이 이미
 * 그 말을 품은 경우(dev 「갈매기카페 2호점(형제)」) 「(형제) (형제)」로 겹친다. 표시가 필요한 것은 **하나뿐인
 * 내 매장**이고, 지금 보는 매장은 목록에서 굵게 그리는 것이 따로 답한다.
 */
export function posSoldoutStoreChoiceLabel(storeName, isSession) {
  return isSession ? `${storeName} (내 매장)` : storeName
}

/** 목록 머리 「메뉴 6개」 */
export function posSoldoutCountText(count, kindLabel) {
  return `${kindLabel} ${count}개`
}

/** 미리보기 한 줄의 현재 상태 */
export function posSoldoutCurrentStateText(stopSelling) {
  return stopSelling ? '현재 품절' : '현재 판매중'
}

/** 옵션 미리보기·행 부제 — 그룹과 적용 메뉴 수 */
export function posSoldoutOptionPreviewDetail(groupName, menuCount) {
  return `${groupName} · 적용 메뉴 ${menuCount}개`
}

/** 진행 드로어 절 제목 */
export function posSoldoutProgressSectionTitle(kindLabel) {
  return `${kindLabel}별 처리 결과`
}

/** 플랫폼 칸의 관측 부기 — 나이만 말한다(§16.278). 옵션은 관측 판매 상태 낱말이 앞에 붙는다 */
export const POS_SOLDOUT_OBSERVED_WORD = Object.freeze({
  soldout: '관측 품절',
  selling: '관측 판매중',
  hidden: '관측 숨김',
  unknown: '관측 모름',
})

export function posSoldoutObservedCaption(stateWord, age) {
  return [stateWord, age ? `관측 ${age}` : null].filter(Boolean).join(' · ')
}

/** 도움말 드로어 「이 화면의 실제 적용 범위」 (D9) — 포함·제외를 화면이 자기 말로 적는다 */
export const POS_SOLDOUT_HELP = Object.freeze([
  {
    title: '메뉴 품절·판매 재개',
    body: '오더엔 메뉴 상태를 기준으로 연결된 배달앱에 요청합니다. 검색, 상태·플랫폼 보기 필터, 여러 건 선택을 제공합니다.',
  },
  {
    title: '옵션 품절·판매 재개',
    body: '옵션그룹과 적용 메뉴를 함께 보여 줍니다. 배달앱에서 옵션을 정확히 찾지 못했거나 같은 이름이 둘 이상이면 그 사실을 부기로 말하되 저장은 막지 않습니다 — 품절은 배달앱에 나가기 전에 오더엔 주문을 먼저 막는 값입니다. 반영은 옵션그룹 단위로 나갑니다.',
  },
  {
    title: '플랫폼별 결과와 읽기 전용 확인',
    body: '플랫폼마다 마지막 작업 상태와 관측 시각을 보여 줍니다. 「배달앱 상태 다시 읽기」는 저장 요청을 만들지 않습니다.',
  },
  {
    title: '이번 버전에서 실행하지 않는 기능',
    excluded: true,
    items: [
      '메뉴·옵션 숨김 및 숨김 해제',
      '옵션 가격·이름 변경, 추가·삭제',
      '플랫폼별 실행 대상 선택',
      '24시간·3일·직접 해제 등 임의 품절 기간',
      '동일 요청의 직접 재전송',
    ],
  },
])
